// Config/masters zod DTOs (S7) — BUILD_SPEC.md §5.1-5.2, §11 Settings. Owned by G13.
// CRUD over the FOUNDATION tables landed by G3 (tenants, sites, cost_centres,
// products, pack_specs, locations, silo_calibrations, users, memberships) — no new
// tables, so every schema here maps directly onto an existing @zebra/db table.
// Consumed by services/api/src/modules/config/ via @hono/zod-validator.
import { z } from 'zod';
import { BASE_UOMS, LOCATION_TYPES, PRODUCT_TYPES, ROLES, SILO_CALIBRATION_METHODS, SILO_DENSITY_SOURCES, VAT_TREATMENTS, } from '../enums.js';
import { moisturePctSchema, packSpecPackagingLineSchema, quantitySchema, ulidSchema } from './common.js';
// ---------------------------------------------------------------------------
// Shared: query-string pagination + fuzzy-ish `q` filter (code/name), and a
// positive-decimal helper for physical quantities (height/volume/density/pack qty)
// that reuses `quantitySchema`'s decimal-string shape but additionally rejects
// zero/negative values, which none of those fields can meaningfully take.
// ---------------------------------------------------------------------------
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    /** Fuzzy-ish filter by code/name — every list endpoint in this module accepts it
     * (goal text: "All list endpoints: cursor pagination + fuzzy-ish filter by
     * code/name"). Implemented as a case-insensitive substring match in the handler
     * (ILIKE '%q%'), not real trigram similarity — that's G19's `/search` endpoint's
     * job (pg_trgm-backed ranking). */
    q: z.string().min(1).max(200).optional(),
});
const positiveDecimalSchema = quantitySchema.refine((v) => Number(v) > 0, 'must be a positive number');
const nonNegativeDecimalSchema = quantitySchema.refine((v) => Number(v) >= 0, 'must not be negative');
const isActiveQueryParamSchema = z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true'));
// ---------------------------------------------------------------------------
// Tenant settings (§5.1, §11 Settings) — GET/PATCH the active tenant's settings
// jsonb: over-receipt tolerance %, invoice footer, other per-tenant defaults.
// ---------------------------------------------------------------------------
/** Known settings keys are validated; unknown keys pass through untouched so a
 * later goal (or a manual ops edit) can extend `tenants.settings` without this
 * schema rejecting the whole PATCH. PATCH semantics are a shallow MERGE (handler
 * spreads the existing jsonb with this partial), never a full replace. */
export const tenantSettingsSchema = z
    .object({
    /** Default 5% per BUILD_SPEC.md §5.4 goods-receipt over-receipt guard. */
    overReceiptTolerancePct: z.number().min(0).max(100).optional(),
    invoiceFooter: z.string().max(2000).nullable().optional(),
    /** Per-tenant module gate (Stuart ruling, 2026-08-26, verbatim: "Silos as a
     * form of warehouse should be able to be configured on or off, with the
     * default being off because we don't have any silos for Howick. If at all,
     * silos should just be a type of warehouse."). ABSENT or `false` = the silo
     * UI/reporting module is hidden entirely for this tenant. Silos remain
     * modelled as ordinary `locations` rows with `type = 'SILO'` either way (see
     * locations.ts) — this flag gates presentation only, no schema change.
     * Default OFF: Howick ships without it; an ADMIN can flip it on later via
     * Settings (TenantSettingsPanel.tsx) for a tenant that does use silos. */
    silosEnabled: z.boolean().optional(),
    defaults: z.record(z.string(), z.unknown()).optional(),
})
    .partial()
    .passthrough();
export const patchTenantSettingsRequestSchema = tenantSettingsSchema;
// ---------------------------------------------------------------------------
// Sites (§5.1) — no `is_active` column on this table (landed schema), so CRUD here
// is create/list/get/update only; no deactivate flag, no delete endpoint (matches
// the established "no hard DELETE" convention — see purchasing/suppliers.ts).
// ---------------------------------------------------------------------------
export const siteCreateSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
});
export const siteUpdateSchema = z.object({
    code: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
});
export const siteListQuerySchema = listQueryBaseSchema;
// ---------------------------------------------------------------------------
// Cost centres (§5.1) — same shape as sites: no `is_active` column, no delete.
// ---------------------------------------------------------------------------
export const costCentreCreateSchema = z.object({
    siteId: ulidSchema,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
});
export const costCentreUpdateSchema = z.object({
    siteId: ulidSchema.optional(),
    code: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
});
export const costCentreListQuerySchema = listQueryBaseSchema.extend({
    siteId: ulidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Locations (§5.2) + silo_calibrations as a nested resource on a SILO-type location.
// ---------------------------------------------------------------------------
export const locationCreateSchema = z.object({
    siteId: ulidSchema,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    type: z.enum(LOCATION_TYPES),
    parentId: ulidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
});
export const locationUpdateSchema = z.object({
    siteId: ulidSchema.optional(),
    code: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
    type: z.enum(LOCATION_TYPES).optional(),
    parentId: ulidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
});
export const locationListQuerySchema = listQueryBaseSchema.extend({
    siteId: ulidSchema.optional(),
    type: z.enum(LOCATION_TYPES).optional(),
    isActive: isActiveQueryParamSchema,
});
const strappingPointSchema = z.object({
    levelM: z.number().nonnegative(),
    volumeM3: z.number().nonnegative(),
});
const siloCalibrationBaseSchema = z.object({
    method: z.enum(SILO_CALIBRATION_METHODS),
    heightM: positiveDecimalSchema.nullable().optional(),
    fullVolumeM3: positiveDecimalSchema.nullable().optional(),
    /** STRAPPING only. Must be supplied pre-sorted, strictly ascending by `levelM`
     * (goal text: "STRAPPING needs a non-empty sorted strapping array") — @zebra/core's
     * `siloMassKg` tolerates unsorted input (it re-sorts defensively), but this is the
     * one place we can catch an operator data-entry mistake (duplicate/out-of-order
     * breakpoints) at write time instead of silently accepting a nonsensical curve. */
    strapping: z.array(strappingPointSchema).nullable().optional(),
    densitySource: z.enum(SILO_DENSITY_SOURCES),
    densityOverrideKgM3: positiveDecimalSchema.nullable().optional(),
});
/** Cross-field rule (goal text): "validate method LINEAR needs height_m+full_volume_m3,
 * STRAPPING needs a non-empty sorted strapping array." Also requires
 * `densityOverrideKgM3` when `densitySource = OVERRIDE` (mirrors the runtime check
 * `services/api/src/modules/silo/readings.ts` already makes when it actually consumes
 * a calibration — this schema catches the same mistake earlier, at config time). */
function checkSiloCalibration(val, ctx) {
    if (val.method === 'LINEAR') {
        if (!val.heightM) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'heightM is required for LINEAR calibration',
                path: ['heightM'],
            });
        }
        if (!val.fullVolumeM3) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'fullVolumeM3 is required for LINEAR calibration',
                path: ['fullVolumeM3'],
            });
        }
    }
    else {
        // STRAPPING
        if (!val.strapping || val.strapping.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'strapping is required (non-empty) for STRAPPING calibration',
                path: ['strapping'],
            });
        }
        else {
            for (let i = 1; i < val.strapping.length; i++) {
                const prev = val.strapping[i - 1];
                const curr = val.strapping[i];
                if (curr.levelM <= prev.levelM) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `strapping must be sorted, strictly ascending by levelM: index ${i} (${curr.levelM}) is not greater than index ${i - 1} (${prev.levelM})`,
                        path: ['strapping', i],
                    });
                }
            }
        }
    }
    if (val.densitySource === 'OVERRIDE' && !val.densityOverrideKgM3) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'densityOverrideKgM3 is required when densitySource is OVERRIDE',
            path: ['densityOverrideKgM3'],
        });
    }
}
export const upsertSiloCalibrationRequestSchema = siloCalibrationBaseSchema.superRefine(checkSiloCalibration);
// ---------------------------------------------------------------------------
// Products (§5.2) + pack_specs as a nested resource on the pack (SKU) product.
// ---------------------------------------------------------------------------
/** Cross-field rule (goal text): "moisture-tracked products may set default_mc_pct" —
 * i.e. a product that is NOT moisture-tracked must not carry one. Applied on create
 * (full picture known); on update it only re-checks when the caller touches either
 * field in the SAME request (same precedent as purchasing's supplier-contract
 * basis/bands rule — re-validating against the DB's current `isMoistureTracked`
 * would need a read this pure zod layer doesn't have). */
function checkMoistureTracking(val, ctx) {
    if (val.defaultMcPct != null && val.isMoistureTracked === false) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'defaultMcPct may only be set when isMoistureTracked is true',
            path: ['defaultMcPct'],
        });
    }
}
export const productCreateSchema = z
    .object({
    sku: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    type: z.enum(PRODUCT_TYPES),
    baseUom: z.enum(BASE_UOMS),
    isMoistureTracked: z.boolean().default(false),
    defaultMcPct: moisturePctSchema.nullable().optional(),
    bulkDensityKgM3: positiveDecimalSchema.nullable().optional(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    minLevel: nonNegativeDecimalSchema.nullable().optional(),
    reorderQty: nonNegativeDecimalSchema.nullable().optional(),
    xeroSalesAccount: z.string().max(200).nullable().optional(),
    xeroPurchaseAccount: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
})
    .superRefine(checkMoistureTracking);
export const productUpdateSchema = z
    .object({
    sku: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
    type: z.enum(PRODUCT_TYPES).optional(),
    baseUom: z.enum(BASE_UOMS).optional(),
    isMoistureTracked: z.boolean().optional(),
    defaultMcPct: moisturePctSchema.nullable().optional(),
    bulkDensityKgM3: positiveDecimalSchema.nullable().optional(),
    vatTreatment: z.enum(VAT_TREATMENTS).optional(),
    minLevel: nonNegativeDecimalSchema.nullable().optional(),
    reorderQty: nonNegativeDecimalSchema.nullable().optional(),
    xeroSalesAccount: z.string().max(200).nullable().optional(),
    xeroPurchaseAccount: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
})
    .superRefine((val, ctx) => {
    if (val.isMoistureTracked === undefined && val.defaultMcPct === undefined)
        return;
    checkMoistureTracking(val, ctx);
});
export const productListQuerySchema = listQueryBaseSchema.extend({
    type: z.enum(PRODUCT_TYPES).optional(),
    isActive: isActiveQueryParamSchema,
});
const qtyPerPackSchema = positiveDecimalSchema;
export const upsertPackSpecRequestSchema = z.object({
    bulkProductId: ulidSchema,
    qtyPerPack: qtyPerPackSchema,
    // Canonical array shape (BUILD_SPEC.md §5.2: "packaging jsonb [(product_id, qty)]")
    // — unified with packconv on `packSpecPackagingLineSchema` (single source of truth
    // in schemas/common.ts, Wave 4 gate 2026-07-07). Was `z.record(...)` (an OBJECT),
    // which rejected the exact array the packconv POST reads, so packaging could never
    // persist and pack conversions silently skipped the packaging-consumable ISSUE leg.
    packaging: z.array(packSpecPackagingLineSchema).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Process stages (§5.5) — GET-ONLY passthrough for settings display. ALL writes
// (create/update/activate-recipe/etc.) belong to the production module (G7) — this
// module deliberately registers no POST/PATCH/DELETE route for process_stages.
// ---------------------------------------------------------------------------
export const processStageListQuerySchema = z.object({
    siteId: ulidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Users / memberships (§5.1, §6) — ADMIN only. Users are auto-provisioned on first
// login (services/api/src/middleware/auth.ts); this module manages membership/role
// in the ACTIVE tenant only, never creates a `users` row itself.
// ---------------------------------------------------------------------------
export const userListQuerySchema = listQueryBaseSchema;
export const membershipCreateSchema = z.object({
    /** Looked up against the existing (global) `users` table — the user must have
     * already signed in at least once for a row to exist; there is no endpoint here
     * that provisions a brand-new `users` row (that stays exclusively the auth
     * middleware's job, keyed on the verified JWT's `sub`, never on a hand-entered
     * email). */
    email: z.string().email(),
    role: z.enum(ROLES),
});
export const membershipUpdateSchema = z.object({
    role: z.enum(ROLES),
});
//# sourceMappingURL=config.js.map